import React, { useEffect } from "react";
import { useContext } from "react";
import { useState } from "react";
import { createContext } from "react";

const DarkModeContext = createContext();

/**
 * Provider component for the DarkModeContext
 *
 * This component manages the theme state and provides a function
 * to toggle between "light", "classic" and "dark" themes
 *
 * @param {React.ReactNode} children - The child components that will have access to the context
 */
function DarkModeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    // Load theme from localStorage
    const storageTheme = localStorage.getItem("benthos-theme");

    // Returns the theme loaded from localStorage. If not, returns the user preference theme.
    return storageTheme
      ? storageTheme
      : window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  // Toggles between the themes
  function toggleTheme(selectedTheme) {
    setTheme(selectedTheme);
  }

  useEffect(() => {
    const themeClasses = {
      dark: "dark-mode",
      light: "light-mode",
      classic: "classic-mode",
    };

    const activeClass = themeClasses[theme];

    if (activeClass) {
      document.documentElement.className = activeClass;
      localStorage.setItem("benthos-theme", theme);
    }
  }, [theme]);

  return (
    <DarkModeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </DarkModeContext.Provider>
  );
}

/**
 * Custom hook to access the DarkModeContext
 * 
 * @returns {{ theme: string, toggleTheme: () => void }} The current theme and the function to toggle it. 
 */
function useDarkMode() {
  const context = useContext(DarkModeContext);

  if (context === undefined)
    throw new Error("DarkModeContext was used outside of DarkModeProvider");

  return context;
}

export { useDarkMode, DarkModeProvider };
